import { supabase } from "../../lib/supabase";

/* ---------------------------------------------------------------- types */

export type CheckStatus = "pass" | "fail" | "na";
export type AuditStatus = "in_progress" | "pass" | "fail" | "partial";
export type CheckType = "fire_alarm" | "emergency_lighting" | "fire_extinguisher" | "racking" | "custom";

export interface CheckDefinition {
  id: number;
  name: string;
  description: string | null;
  active: boolean;
  sort_order: number;
}

export interface WeeklyAudit {
  id: number;
  week_commencing: string;
  auditor_name: string;
  overall_status: AuditStatus;
  notes: string | null;
  completed_date: string | null;
  created_at: string;
}

export interface CheckItem {
  id: number;
  audit_id: number;
  check_type: CheckType;
  item_label: string | null;
  zone: string | null;
  rack_number: string | null;
  custom_check_id: number | null;
  status: CheckStatus;
  notes: string | null;
  sort_order: number;
}

/** One row of v_hs_weekly_compliance — the audit plus its counted result. */
export interface WeeklyCompliance {
  audit_id: number;
  week_commencing: string;
  auditor_name: string;
  overall_status: AuditStatus;
  completed_date: string | null;
  checks_total: number;
  checks_passed: number;
  checks_failed: number;
  checks_na: number;
  /** Null when nothing was actually assessed — a week of pure N/A has no percentage. */
  compliance_pct: number | null;
}

export interface AnnualService {
  id: number;
  item_name: string;
  last_inspection_date: string;
  renewal_period_years: number;
  next_renewal_date: string;
  company: string | null;
  company_contact: string | null;
  company_email: string | null;
  notes: string | null;
  days_to_renewal: number;
  renewal_status: "overdue" | "due_soon" | "current";
  document_count: number;
}

export interface ServiceDocument {
  id: number;
  service_id: number;
  file_name: string;
  storage_path: string;
  uploaded_date: string;
}

/** What a form hands back for one check before it has an id. */
export interface NewCheckItem {
  check_type: CheckType;
  item_label?: string | null;
  zone?: string | null;
  rack_number?: string | null;
  custom_check_id?: number | null;
  status: CheckStatus;
  notes?: string | null;
  sort_order: number;
}

/* ------------------------------------------------------- weekly audits */

export async function fetchWeeklyCompliance(limit = 52): Promise<WeeklyCompliance[]> {
  const { data, error } = await supabase
    .from("v_hs_weekly_compliance")
    .select("*")
    .order("week_commencing", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as WeeklyCompliance[];
}

/**
 * Compliance across a date window, for the report. Bounded by week rather
 * than by created_at: an audit recorded late still belongs to the week it
 * was walked, which is the week the report is asked about.
 */
export async function fetchComplianceRange(from: string, to: string): Promise<WeeklyCompliance[]> {
  const { data, error } = await supabase
    .from("v_hs_weekly_compliance")
    .select("*")
    .gte("week_commencing", from)
    .lte("week_commencing", to)
    .order("week_commencing", { ascending: false });
  if (error) throw error;
  return (data ?? []) as WeeklyCompliance[];
}

export async function fetchAudit(id: number): Promise<WeeklyAudit> {
  const { data, error } = await supabase.from("hs_weekly_audits").select("*").eq("id", id).single();
  if (error) throw error;
  return data as WeeklyAudit;
}

export async function fetchAuditItems(auditId: number): Promise<CheckItem[]> {
  const { data, error } = await supabase
    .from("hs_check_items")
    .select("*")
    .eq("audit_id", auditId)
    .order("check_type")
    .order("sort_order");
  if (error) throw error;
  return (data ?? []) as CheckItem[];
}

/** The Monday already audited, so the form can refuse a duplicate week up front. */
export async function fetchAuditedWeeks(): Promise<string[]> {
  const { data, error } = await supabase
    .from("hs_weekly_audits")
    .select("week_commencing")
    .order("week_commencing", { ascending: false })
    .limit(104);
  if (error) throw error;
  return (data ?? []).map((r) => (r as { week_commencing: string }).week_commencing);
}

/**
 * Saves an audit and its checks together. The audit row goes first because
 * the items need its id; if the items then fail, the audit is removed again
 * rather than left behind as an empty week that would read as "in progress"
 * forever and block the unique index on a genuine retry.
 */
export async function createAudit(
  audit: { week_commencing: string; auditor_name: string; notes: string | null; completed_date: string | null },
  items: NewCheckItem[],
  createdBy: string,
): Promise<number> {
  const { data, error } = await supabase
    .from("hs_weekly_audits")
    .insert({ ...audit, created_by: createdBy })
    .select("id")
    .single();
  if (error) throw error;
  const auditId = (data as { id: number }).id;

  if (items.length > 0) {
    const { error: itemErr } = await supabase
      .from("hs_check_items")
      .insert(items.map((i) => ({ ...i, audit_id: auditId })));
    if (itemErr) {
      await supabase.from("hs_weekly_audits").delete().eq("id", auditId);
      throw itemErr;
    }
  }
  return auditId;
}

export async function updateAudit(
  id: number,
  patch: Partial<Pick<WeeklyAudit, "auditor_name" | "notes" | "completed_date">>,
): Promise<void> {
  const { error } = await supabase.from("hs_weekly_audits").update(patch).eq("id", id);
  if (error) throw error;
}

/**
 * Replaces an audit's checks wholesale. The overall status is recalculated
 * by trigger from whatever lands, so it is never passed in here.
 */
export async function replaceAuditItems(auditId: number, items: NewCheckItem[]): Promise<void> {
  const { error: delErr } = await supabase.from("hs_check_items").delete().eq("audit_id", auditId);
  if (delErr) throw delErr;
  if (items.length === 0) return;
  const { error } = await supabase
    .from("hs_check_items")
    .insert(items.map((i) => ({ ...i, audit_id: auditId })));
  if (error) throw error;
}

export async function deleteAudit(id: number): Promise<void> {
  const { error } = await supabase.from("hs_weekly_audits").delete().eq("id", id);
  if (error) throw error;
}

/* --------------------------------------------------- check definitions */

export async function fetchCheckDefinitions(includeInactive = false): Promise<CheckDefinition[]> {
  let q = supabase.from("hs_weekly_check_definitions").select("*").order("sort_order").order("name");
  if (!includeInactive) q = q.eq("active", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as CheckDefinition[];
}

export async function createCheckDefinition(
  name: string,
  description: string | null,
  createdBy: string,
): Promise<void> {
  const { error } = await supabase
    .from("hs_weekly_check_definitions")
    .insert({ name: name.trim(), description, created_by: createdBy });
  if (error) throw error;
}

/** Deactivated, not deleted — historic audits keep the meaning of their custom items. */
export async function setCheckDefinitionActive(id: number, active: boolean): Promise<void> {
  const { error } = await supabase.from("hs_weekly_check_definitions").update({ active }).eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------------ annual services */

export async function fetchAnnualServices(): Promise<AnnualService[]> {
  const { data, error } = await supabase
    .from("v_hs_annual_services")
    .select("*")
    .order("next_renewal_date");
  if (error) throw error;
  return (data ?? []) as AnnualService[];
}

export async function fetchAnnualService(id: number): Promise<AnnualService> {
  const { data, error } = await supabase.from("v_hs_annual_services").select("*").eq("id", id).single();
  if (error) throw error;
  return data as AnnualService;
}

export interface AnnualServiceInput {
  item_name: string;
  last_inspection_date: string;
  renewal_period_years: number;
  company: string | null;
  company_contact: string | null;
  company_email: string | null;
  notes: string | null;
}

export async function createAnnualService(input: AnnualServiceInput, createdBy: string): Promise<number> {
  const { data, error } = await supabase
    .from("hs_annual_services")
    .insert({ ...input, created_by: createdBy })
    .select("id")
    .single();
  if (error) throw error;
  return (data as { id: number }).id;
}

export async function updateAnnualService(id: number, input: AnnualServiceInput): Promise<void> {
  const { error } = await supabase
    .from("hs_annual_services")
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteAnnualService(id: number): Promise<void> {
  const { error } = await supabase.from("hs_annual_services").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------------- certification */

export async function fetchServiceDocuments(serviceId: number): Promise<ServiceDocument[]> {
  const { data, error } = await supabase
    .from("hs_annual_service_documents")
    .select("*")
    .eq("service_id", serviceId)
    .order("uploaded_date", { ascending: false });
  if (error) throw error;
  return (data ?? []) as ServiceDocument[];
}

/**
 * Uploads to the private hs-certificates bucket and records the path. The
 * row stores the path rather than a URL because links to a private bucket
 * are signed on demand and expire — a stored URL would read as a working
 * certificate long after it stopped being one.
 */
export async function uploadServiceDocument(serviceId: number, file: File, uploadedBy: string): Promise<void> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `annual-services/${serviceId}/${Date.now()}-${safeName}`;
  const { error: upErr } = await supabase.storage
    .from("hs-certificates")
    .upload(path, file, { contentType: file.type || "application/pdf" });
  if (upErr) throw upErr;

  const { error } = await supabase.from("hs_annual_service_documents").insert({
    service_id: serviceId,
    file_name: file.name,
    storage_path: path,
    created_by: uploadedBy,
  });
  if (error) throw error;
}

export async function signedCertificateUrl(storagePath: string, downloadAs?: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from("hs-certificates")
    .createSignedUrl(storagePath, 300, downloadAs ? { download: downloadAs } : undefined);
  if (error) throw error;
  return data.signedUrl;
}

/* ---------------------------------------------------------- the report */

export interface ComplianceSummary {
  period: { from: string; to: string };
  weeks_total: number;
  weeks_recorded: number;
  /** Every Monday in the period with nothing recorded against it. */
  weeks_missed: string[];
  coverage_pct: number | null;
  average_compliance_pct: number | null;
  checks_total: number;
  checks_passed: number;
  checks_failed: number;
  checks_na: number;
  audits: WeeklyCompliance[];
  failures: {
    id: number;
    check_type: CheckType;
    item_label: string | null;
    zone: string | null;
    rack_number: string | null;
    notes: string | null;
    week_commencing: string;
    custom_name: string | null;
  }[];
  annual_services: AnnualService[];
}

/**
 * The compliance position, counted in the database. The PDF reads the same
 * function, so the report and the screen cannot report a different number
 * of missed weeks.
 */
export async function fetchComplianceSummary(from: string, to: string): Promise<ComplianceSummary> {
  const { data, error } = await supabase.rpc("hs_compliance_summary", { p_from: from, p_to: to });
  if (error) throw error;
  return data as ComplianceSummary;
}

export interface IssuedReport {
  id: number;
  period_start: string;
  period_end: string;
  issued_at: string;
  storage_path: string;
}

export async function fetchIssuedReports(limit = 20): Promise<IssuedReport[]> {
  const { data, error } = await supabase
    .from("hs_compliance_reports")
    .select("id, period_start, period_end, issued_at, files(storage_path)")
    .order("issued_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => {
    const row = r as unknown as Omit<IssuedReport, "storage_path"> & { files: { storage_path: string } | null };
    return { ...row, storage_path: row.files?.storage_path ?? "" };
  });
}

export async function signedReportUrl(storagePath: string, downloadAs?: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from("documents")
    .createSignedUrl(storagePath, 300, downloadAs ? { download: downloadAs } : undefined);
  if (error) throw error;
  return data.signedUrl;
}

const REPORT_POLL_MS = 1500;
const REPORT_POLL_LIMIT = 40; // ≤ 60s

/**
 * Renders and persists the report through generate-hs-report, relayed the
 * same way as every other report button in this app — managed devices in
 * this fleet block direct browser POSTs to /functions/v1, so the request
 * goes out through pg_net and the result is polled back.
 */
export async function generateComplianceReport(from: string, to: string): Promise<IssuedReport> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: "generate-hs-report",
    p_body: { period_start: from, period_end: to },
  });
  if (error) throw new Error(error.message);

  let outcome: { done: boolean; status?: number; body?: { ok?: boolean; error?: string; report_id?: number } } = {
    done: false,
  };
  for (let i = 0; i < REPORT_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, REPORT_POLL_MS));
    const { data, error: pollErr } = await supabase.rpc("manual_sync_result", { p_request_id: requestId });
    if (pollErr) throw new Error(pollErr.message);
    outcome = (data ?? { done: false }) as typeof outcome;
  }
  if (!outcome.done) throw new Error("The report is taking longer than expected — check back shortly.");

  const respBody = outcome.body ?? {};
  if (respBody.error) throw new Error(respBody.error);
  if (outcome.status !== undefined && outcome.status >= 400) throw new Error(`Report generation failed (${outcome.status}).`);

  const { data: report, error: repErr } = await supabase
    .from("hs_compliance_reports")
    .select("id, period_start, period_end, issued_at, files(storage_path)")
    .eq("id", respBody.report_id!)
    .single();
  if (repErr) throw repErr;
  const row = report as unknown as Omit<IssuedReport, "storage_path"> & { files: { storage_path: string } | null };
  return { ...row, storage_path: row.files?.storage_path ?? "" };
}
